/**
 * Shared helper functions for parallel stage loading state management.
 *
 * These utilities support components that load data in multiple parallel stages,
 * tracking progress through activeStages and completedStages arrays. They are
 * designed to be generic and reusable across different loading pipelines.
 */

/**
 * Checklist item visual state constants.
 * Used by getChecklistItemState to indicate how a stage should be displayed.
 */
export const CHECKLIST_ITEM_STATE_INCOMPLETE = 'incomplete';
export const CHECKLIST_ITEM_STATE_ACTIVE = 'active';
export const CHECKLIST_ITEM_STATE_COMPLETE = 'complete';

/**
 * Default minimum progress value to show (avoids 0% which looks like nothing started).
 */
const MIN_PROGRESS = 5;

/**
 * Maximum progress value (100%).
 */
const MAX_PROGRESS = 100;

/**
 * Calculates progress percentage based on completed stages and their weights.
 * @param {string[]} completedStages - Array of completed stage keys.
 * @param {{[key: string]: number}} stageProgressWeights - Object mapping stage keys to their
 *   progress weight values. Weights should sum to 100 for accurate percentage reporting.
 * @returns {number} Progress percentage clamped between MIN_PROGRESS (5) and MAX_PROGRESS (100).
 * @example
 * const weights = { METERS: 20, RULES: 30, TRADES: 50 };
 * calculateProgress(['METERS', 'RULES'], weights); // returns 50
 */
export const calculateProgress = (completedStages = [], stageProgressWeights = {}) => {
  const completedProgress = completedStages.reduce(
    (sum, stageKey) => sum + (stageProgressWeights[stageKey] || 0),
    0,
  );
  return Math.max(MIN_PROGRESS, Math.min(completedProgress, MAX_PROGRESS));
};

/**
 * Determines the visual state for a checklist item based on stage tracking arrays.
 * @param {string} stageKey - The stage key to check.
 * @param {string[]} activeStages - Array of currently active (in-progress) stage keys.
 * @param {string[]} completedStages - Array of completed stage keys.
 * @returns {string} One of CHECKLIST_ITEM_STATE_COMPLETE, CHECKLIST_ITEM_STATE_ACTIVE,
 *   or CHECKLIST_ITEM_STATE_INCOMPLETE.
 * @example
 * getChecklistItemState('RULES', ['RULES', 'TRADES'], ['METERS']); // returns 'active'
 * getChecklistItemState('METERS', ['RULES'], ['METERS']); // returns 'complete'
 * getChecklistItemState('DATA', [], ['METERS']); // returns 'incomplete'
 */
export const getChecklistItemState = (stageKey, activeStages, completedStages) => {
  if ((completedStages || []).includes(stageKey)) {
    return CHECKLIST_ITEM_STATE_COMPLETE;
  }
  if ((activeStages || []).includes(stageKey)) {
    return CHECKLIST_ITEM_STATE_ACTIVE;
  }
  return CHECKLIST_ITEM_STATE_INCOMPLETE;
};

/**
 * Immutably adds a stage key to an array if not already present.
 * Typically used to add a stage to the completedStages array.
 * @param {string[]} stages - The current stages array.
 * @param {string} key - The stage key to add.
 * @returns {string[]} A new array with the key added, or the same array if already present.
 * @example
 * addStageToCompleted(['METERS'], 'RULES'); // returns ['METERS', 'RULES']
 * addStageToCompleted(['METERS', 'RULES'], 'RULES'); // returns ['METERS', 'RULES'] (unchanged)
 */
export const addStageToCompleted = (stages, key) => (
  stages.includes(key) ? stages : [...stages, key]
);

/**
 * Immutably removes a stage key from an array.
 * Typically used to remove a stage from the activeStages array when it completes.
 * @param {string[]} stages - The current stages array.
 * @param {string} key - The stage key to remove.
 * @returns {string[]} A new array without the specified key.
 * @example
 * removeStageFromActive(['RULES', 'TRADES'], 'RULES'); // returns ['TRADES']
 * removeStageFromActive(['TRADES'], 'RULES'); // returns ['TRADES'] (no change)
 */
export const removeStageFromActive = (stages, key) => (
  (stages || []).filter((s) => s !== key)
);

/**
 * Checks whether all specified parallel stages are present in completedStages.
 * Used as a gate condition to determine when parallel loading phases are complete.
 * @param {string[]} completedStages - Array of completed stage keys.
 * @param {string[]} parallelStages - Array of stage keys that must all be completed.
 * @returns {boolean} True if all parallelStages are in completedStages.
 * @example
 * isParallelStagesCompleted(['METERS', 'RULES'], ['RULES', 'TRADES']); // returns false
 * isParallelStagesCompleted(['METERS', 'RULES', 'TRADES'], ['RULES', 'TRADES']); // true
 */
export const isParallelStagesCompleted = (completedStages = [], parallelStages = []) => (
  parallelStages.every((stage) => completedStages.includes(stage))
);
